import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import Loader from "../../components/Loader/Loader";
import {
  getStamps,
  getFilterOptions,
  reorderStamps,
} from "../../utilities/stamps-service";
import StampItem from "./StampItem";
import SortableStampItem from "./SortableStampItem";

const FILTER_TYPES = ["decade", "theme", "colour"];
const PAGE_SIZE = 30;

export default function StampIndexPage({ user }) {
  const [searchParams] = useSearchParams();
  const [stamps, setStamps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeFilter, setActiveFilter] = useState(() => {
    if (searchParams.get("decade") || searchParams.get("year")) return "decade";
    if (searchParams.get("theme")) return "theme";
    if (searchParams.get("colour")) return "colour";
    return null;
  });
  const [filterValues, setFilterValues] = useState(() => {
    const initial =
      searchParams.get("decade") ||
      searchParams.get("year") ||
      searchParams.get("theme") ||
      searchParams.get("colour") ||
      "";
    return initial ? [initial] : [];
  });
  const [filterOptionsMap, setFilterOptionsMap] = useState({
    decade: [],
    theme: [],
    colour: [],
  });

  const sentinelRef = useRef(null);

  // Reorder mode state
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderStampsState, setReorderStampsState] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const hasActiveFilter = filterValues.length > 0;
  const isCanonicalView = !hasActiveFilter;

  // Fetch filter options once on mount
  useEffect(() => {
    async function fetchFilterOptions() {
      const response = await getFilterOptions();
      if (response && !response.message) {
        setFilterOptionsMap({
          decade: response.decades || [],
          theme: response.themes || [],
          colour: response.colours || [],
        });
      }
    }
    fetchFilterOptions();
  }, []);

  // Build query params from active filter
  const buildParams = useCallback(
    (pageNum) => {
      const params = new URLSearchParams();
      params.set("page", pageNum);
      params.set("limit", PAGE_SIZE);
      if (activeFilter && filterValues.length > 0) {
        const key = activeFilter === "decade" ? "decade" : activeFilter;
        filterValues.forEach((v) => {
          params.append(
            key,
            activeFilter === "decade" ? v.replace("s", "") : v,
          );
        });
      }
      return params;
    },
    [activeFilter, filterValues],
  );

  // Fetch stamps for a given page (initial or next)
  const fetchPage = useCallback(
    async (pageNum, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const response = await getStamps(buildParams(pageNum));

      if (response && response.stamps) {
        setStamps((prev) =>
          append ? [...prev, ...response.stamps] : response.stamps,
        );
        setHasMore(response.hasMore);
        setPage(pageNum);
      } else {
        console.log("error: ", response);
      }

      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    },
    [buildParams],
  );

  // Initial load and filter changes: reset to page 1
  useEffect(() => {
    setStamps([]);
    setPage(1);
    setHasMore(true);
    fetchPage(1, false);
  }, [fetchPage]);

  // Intersection Observer for endless scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore &&
          !isReorderMode
        ) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: "400px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, page, fetchPage, isReorderMode]);

  function handleNavClick(filter) {
    if (activeFilter === filter) {
      setActiveFilter(null);
      setFilterValues([]);
    } else {
      setActiveFilter(filter);
      setFilterValues([]);
    }
  }

  function handleFilterSelect(type, value) {
    if (activeFilter !== type) {
      // Switching category — start fresh with this value
      setActiveFilter(type);
      setFilterValues([value]);
    } else if (filterValues.includes(value)) {
      // Toggle off within same category
      const next = filterValues.filter((v) => v !== value);
      setFilterValues(next);
    } else {
      // Add another value within same category
      setFilterValues([...filterValues, value]);
    }
  }

  function clearFilters() {
    setFilterValues([]);
    setActiveFilter(null);
  }

  function enterReorderMode() {
    setReorderStampsState([...stamps]);
    setIsReorderMode(true);
  }

  function cancelReorderMode() {
    setReorderStampsState([]);
    setIsReorderMode(false);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setReorderStampsState((items) => {
      const oldIndex = items.findIndex((s) => s._id === active.id);
      const newIndex = items.findIndex((s) => s._id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }

  async function saveReorder() {
    setIsSavingOrder(true);
    try {
      // Assign sortOrder with gaps of 10, using the first stamp's original sortOrder as base
      const baseOrder = reorderStampsState[0]?.sortOrder || 10;
      const updates = reorderStampsState.map((stamp, index) => ({
        _id: stamp._id,
        sortOrder: baseOrder + index * 10,
      }));

      const result = await reorderStamps(updates);
      if (result?.success) {
        // Update local stamps state with new order
        const updatedStamps = reorderStampsState.map((stamp, index) => ({
          ...stamp,
          sortOrder: baseOrder + index * 10,
        }));
        setStamps(updatedStamps);
        setIsReorderMode(false);
        setReorderStampsState([]);
      } else {
        console.error("Failed to save order:", result);
      }
    } catch (err) {
      console.error("Failed to save order:", err);
    }
    setIsSavingOrder(false);
  }

  return (
    <>
      {/* Description on white background */}
      <section className="w-full bg-background pb-2">
        <div className="max-w-7xl mx-auto px-6 pt-2 pb-8">
          <p className="text-black text-base leading-relaxed max-w-2xl">
            <strong>
              A curated archive of Japanese stamps, both unused and postmarked.
            </strong>
            <br />
            Each item has been carefully digitized and supplemented with notes,
            with an emphasis on design and cultural insight rather than market
            value. Comments and suggestions are welcome at{" "}
            <a
              href="mailto:contact@posuto.ca"
              className="underline hover:text-neutral-900 text-neutral-500"
            >
              contact@posuto.ca
            </a>
            .
          </p>
        </div>
      </section>

      <section className="w-full bg-neutral-950 min-h-screen">
        {/* Filter Nav Buttons */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex items-center gap-2 flex-wrap">
          {!isReorderMode && (
            <>
              {FILTER_TYPES.map((filter) => (
                <button
                  key={filter}
                  onClick={() => handleNavClick(filter)}
                  className={`px-5 py-2 rounded-full text-base font-medium transition-colors cursor-pointer ${
                    activeFilter === filter && filterValues.length > 0
                      ? "bg-white text-neutral-900"
                      : activeFilter === filter
                        ? "bg-neutral-600 text-white"
                        : "bg-neutral-700 text-neutral-300 hover:bg-neutral-600 hover:text-white"
                  }`}
                >
                  {filter === "decade"
                    ? "Year"
                    : filter.charAt(0).toUpperCase() + filter.slice(1)}
                  {activeFilter === filter &&
                    filterValues.length > 0 &&
                    `: ${filterValues.join(", ")}`}
                </button>
              ))}
              {hasActiveFilter && (
                <button
                  onClick={clearFilters}
                  className="text-base text-neutral-400 hover:text-white underline cursor-pointer ml-1"
                >
                  Clear
                </button>
              )}
            </>
          )}
          {/* Reorder controls (admin only, canonical view only) */}
          {user?.isAdmin && !isReorderMode && isCanonicalView && (
            <button
              onClick={enterReorderMode}
              className="ml-auto px-5 py-2 rounded-full text-base font-medium bg-neutral-700 text-neutral-300 hover:bg-neutral-600 hover:text-white transition-colors cursor-pointer"
            >
              Edit order
            </button>
          )}
          {isReorderMode && (
            <div className="flex items-center gap-2 w-full">
              <span className="text-neutral-300 text-base font-medium">
                Drag stamps to reorder
              </span>
              <div className="ml-auto flex gap-2">
                <button
                  onClick={cancelReorderMode}
                  disabled={isSavingOrder}
                  className="px-5 py-2 rounded-full text-base font-medium bg-neutral-700 text-neutral-300 hover:bg-neutral-600 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveReorder}
                  disabled={isSavingOrder}
                  className="px-5 py-2 rounded-full text-base font-medium bg-white text-neutral-900 hover:bg-neutral-200 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isSavingOrder ? "Saving..." : "Save Order"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Filter Options Panel */}
        {!isReorderMode &&
          activeFilter &&
          filterOptionsMap[activeFilter]?.length > 0 && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
              <div className="flex flex-wrap gap-2">
                {filterOptionsMap[activeFilter].map((option) => (
                  <button
                    key={option}
                    onClick={() => handleFilterSelect(activeFilter, option)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                      filterValues.includes(option)
                        ? "bg-white text-neutral-900"
                        : "bg-neutral-800 text-neutral-400 hover:bg-neutral-700 hover:text-white border border-neutral-700"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

        {/* Stamp Grid */}
        <div className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Loader />
              </div>
            ) : stamps.length === 0 ? (
              <div className="flex justify-center items-center py-20">
                <p className="text-neutral-400 text-base">
                  No stamps match the selected filters.
                </p>
              </div>
            ) : (
              <>
                {isReorderMode ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={reorderStampsState.map((s) => s._id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
                        {reorderStampsState.map((stamp) => (
                          <SortableStampItem key={stamp._id} stamp={stamp} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
                    {stamps.map((stamp, idx) => (
                      <StampItem key={`${stamp._id}-${idx}`} stamp={stamp} />
                    ))}
                  </div>
                )}
                {/* Sentinel element for infinite scroll */}
                <div ref={sentinelRef} className="h-1" />
                {isLoadingMore && (
                  <div className="flex justify-center items-center py-10">
                    <Loader />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
